import { InfiniteData } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Event } from '@/Domain/api/Conversation/dto/Event.dto';
import { Paginated } from '@/Domain/api/Shared/dto/Paginated.dto';
import { AssistedOrderItem } from '@/Domain/components/Inbox/Message/AssistedOrderItem';
import { LinkClickItem } from '@/Domain/components/Inbox/Message/LinkClickItem';
import { LogItem } from '@/Domain/components/Inbox/Message/LogItem';
import { MessageItem } from '@/Domain/components/Inbox/Message/MessageItem';
import { NoteItem } from '@/Domain/components/Inbox/Message/NoteItem';
import { TimestampItem } from '@/Domain/components/Inbox/Message/TimestampItem';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { EventTypeEnum } from '@/Domain/enum/EventType.enum';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';
import { useGetConversation } from '@/Domain/hooks/Conversation/useGetConversation.hook';

type Props = {
  id: string;
  events: InfiniteData<Paginated<Event[]>, unknown> | undefined;
  loading: boolean;
  hasNextPage: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
};

export const MessageList = ({ events, onLoadMore, isLoading, loading, hasNextPage, id }: Props) => {
  const messagesCountRef = useRef<number>(0);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const { conversation } = useGetConversation(id);

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore,
  });

  // flatMap returns a fresh array, so this in-place reverse never mutates a cached page.
  const allEvents = useMemo(
    () => (events?.pages ?? []).flatMap((page) => page?.items ?? []).reverse(),
    [events],
  );

  const scrollToBottom = (): void => {
    if (messagesEndRef.current !== null) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useEffect(() => {
    if (messagesCountRef.current !== allEvents.length) {
      messagesCountRef.current = allEvents.length;
      scrollToBottom();
    }
  }, [allEvents]);

  return (
    <ul className="list-none m-0 flex-grow overflow-y-auto flex flex-col gap-8 hide-scrollbar">
      {allEvents.map((event, index) => {
        if (event.event_type === EventTypeEnum.MESSAGE) {
          return (
            <MessageItem
              key={event.data.id}
              message={event.data}
              conversationId={conversation?.id}
              visitorLabel={conversation?.visitor_label}
              previousMessage={
                index > 0 ? ConversationHelper.getPreviousMessage(allEvents, index) : null
              }
            />
          );
        }

        if (event.event_type === EventTypeEnum.NOTE) {
          return <NoteItem key={event.created_at} note={event.data} />;
        }

        if (event.event_type === EventTypeEnum.LOG) {
          return <LogItem key={event.created_at} log={event.data} />;
        }

        if (event.event_type === EventTypeEnum.TIMESTAMP) {
          return <TimestampItem key={event.created_at} timestamp={event.created_at} />;
        }

        if (event.event_type === EventTypeEnum.LINK_CLICKED) {
          return (
            <LinkClickItem
              key={`${event.created_at}-${event.data.tracked_url_id}`}
              linkClick={event.data}
              timestamp={event.created_at}
            />
          );
        }

        if (event.event_type === EventTypeEnum.ASSISTED_ORDER) {
          return (
            <AssistedOrderItem
              key={`assisted-order-${event.data.id}`}
              assistedOrder={event.data}
              timestamp={event.created_at}
            />
          );
        }

        return null;
      })}

      {(loading || hasNextPage || isLoading) && (
        <li ref={sentryRef} className="mx-6">
          <div className="flex justify-center items-center py-6">
            <Spinner />
          </div>
        </li>
      )}
      <div ref={messagesEndRef} />
    </ul>
  );
};
